import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Telescope, LogOut, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getProfile } from '../services/api';

const Header: React.FC = () => {
  const { isAuthenticated, displayName, signOut, signIn, getIdToken } = useAuth();
  const nav = useNavigate();

  // Stacking-minutes balance chip. Fetched once per header mount so
  // navigating between pages doesn't spam /profile; the Dashboard tile
  // is authoritative for a live reading. Failure is non-fatal — the
  // chip just doesn't render.
  const [balance, setBalance] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const p = await getProfile({ token });
        if (!cancelled) setBalance(p.stackingMinutes.balance);
      } catch {
        /* non-fatal — chip stays hidden */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, getIdToken]);

  return (
    <header className="relative z-20 flex items-center justify-between px-6 py-5 md:px-10">
      <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2 group">
        <div className="relative">
          <Telescope className="h-6 w-6 text-nebula-300 group-hover:text-nebula-200 transition-colors" />
          <div className="absolute inset-0 blur-xl bg-nebula-500/40 -z-10" />
        </div>
        <span className="font-display text-lg font-semibold tracking-tight">
          Pancake<span className="text-nebula-300">Stack</span>
        </span>
      </Link>

      {/* whitespace-nowrap on every nav button so labels can't wrap mid-word
          on tight widths. Sign out drops to icon-only below md so the row
          fits at 375px even with the workspace + tool links present. */}
      <nav className="flex items-center gap-1 md:gap-4">
        {isAuthenticated && (
          <>
            <button
              onClick={() => nav('/dashboard')}
              className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-2 md:px-3 py-1.5"
            >
              Dashboard
            </button>
            <button
              onClick={() => nav('/collections')}
              className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-2 md:px-3 py-1.5"
            >
              Collections
            </button>
            <button
              onClick={() => nav('/jobs')}
              className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-2 md:px-3 py-1.5"
            >
              Stacks
            </button>
          </>
        )}
        <Link
          to="/try"
          className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-2 md:px-3 py-1.5"
        >
          FITS-preview
        </Link>
        <a
          href="/sky/"
          className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-2 md:px-3 py-1.5"
        >
          Sky
        </a>
        {isAuthenticated ? (
          <>
            {balance !== null && (
              <Link
                to="/profile"
                title={`${balance.toLocaleString()} stacking minutes remaining`}
                className={`whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                  balance < 0
                    ? 'border-red-400/40 bg-red-500/10 text-red-200 hover:bg-red-500/15'
                    : balance < 30
                    ? 'border-amber-400/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15'
                    : 'border-white/10 bg-white/[0.04] text-white/75 hover:bg-white/[0.08]'
                }`}
              >
                <Zap className="h-3.5 w-3.5" />
                <span className="tabular-nums">{balance.toLocaleString()}</span>
                <span className="opacity-60 hidden sm:inline">min</span>
              </Link>
            )}
            <button
              onClick={() => nav('/profile')}
              className="hidden md:block whitespace-nowrap text-sm text-white/50 hover:text-white transition-colors"
            >
              {displayName()}
            </button>
            <button
              onClick={signOut}
              aria-label="Sign out"
              title="Sign out"
              className="flex items-center gap-1.5 whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-2 md:px-3 py-1.5"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden md:inline">Sign out</span>
            </button>
          </>
        ) : (
          <button
            onClick={signIn}
            className="whitespace-nowrap px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-sm font-medium transition-colors border border-white/10"
          >
            Sign in
          </button>
        )}
      </nav>
    </header>
  );
};

export default Header;
